import {TailwindContents} from '@wandb/weave/components/Tailwind';
import * as React from 'react';
// eslint-disable-next-line wandb/no-deprecated-imports
import styled from 'styled-components';
import useResizeObserver from 'use-resize-observer';

import {LegendPosition} from '../../util/plotHelpers/legendTypes';
import {InstrumentedWaveLoader} from '../utility/InstrumentedLoader';

type PlotLayoutProps = {
  fontSize: number;
  Graph: React.ReactNode;
  handleHover: (isHovered: boolean) => void;
  Legend: React.ReactNode;
  legendPosition: LegendPosition;
  loading: boolean;
  numLines: number;
  showLegend: boolean;
  Title: React.ReactNode;
  Warning: React.ReactNode;
};

const Wrapper = styled.div<
  Pick<PlotLayoutProps, 'legendPosition' | 'showLegend' | 'fontSize'>
>`
  display: grid;
  height: 100%;
  column-gap: 12px;
  row-gap: 2px;
  position: relative;

  ${({fontSize, legendPosition, showLegend}) => {
    // we show four rows of legend items, plus some space for the row gaps
    const legendHeight = fontSize * 4 + 8;

    if (!showLegend) {
      switch (legendPosition) {
        case 'north':
          return `
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto fit-content(14px) 1fr;
          `;
        case 'south':
          return `
            grid-template-columns: 1fr 1fr;
            grid-template-rows: auto fit-content(14px) 1fr;
          `;
        case 'west':
          return `
            grid-template-columns: 1fr;
            grid-template-rows: auto fit-content(14px) 1fr;
          `;
        case 'east':
          return `
            grid-template-columns: 1fr;
            grid-template-rows: auto fit-content(14px) 1fr;
          `;
      }
    }
    switch (legendPosition) {
      case 'north':
        return `
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto fit-content(14px) fit-content(${legendHeight}px) 1fr;
        `;
      case 'south':
        return `
          grid-template-columns: 1fr 1fr;
          grid-template-rows: auto fit-content(14px) 1fr fit-content(${legendHeight}px);
        `;
      case 'west':
        return `
          grid-template-columns: minmax(auto, 3fr) 7fr;
          grid-template-rows: auto fit-content(14px) 1fr;
        `;
      case 'east':
        return `
          grid-template-columns: 7fr minmax(auto, 3fr);
          grid-template-rows: auto fit-content(14px) 1fr;
        `;
    }
  }}
`;

const GraphWrapper = styled.div<
  Pick<PlotLayoutProps, 'legendPosition' | 'showLegend'>
>`
  cursor: crosshair;
  height: 100%;
  position: relative;
  width: 100%;
  ${({legendPosition, showLegend}) => {
    if (!showLegend) {
      switch (legendPosition) {
        case 'north':
        case 'south':
          return `
            grid-column: 1 / span 2;
            grid-row: 3;
          `;
        case 'west':
          return `
            grid-column: 1;
            grid-row: 3;
          `;
        case 'east':
          return `
            grid-column: 1;
            grid-row: 3;
          `;
        default:
          return ``;
      }
    }
    switch (legendPosition) {
      case 'north':
        return `
          grid-column: 1 / span 2;
          grid-row: 4;
        `;

      case 'south':
        return `
          grid-column: 1 / span 2;
          grid-row: 3;
        `;
      case 'west':
        return `
          grid-column: 2;
          grid-row: 3;
        `;
      case 'east':
        return `
          grid-column: 1;
          grid-row: 3;
        `;
      default:
        return ``;
    }
  }}
`;
const LegendWrapper = styled.div<Pick<PlotLayoutProps, 'legendPosition'>>`
  overflow: auto;

  ${({legendPosition}) => {
    switch (legendPosition) {
      case 'north':
        return `
          display: flex;
          flex-wrap: wrap;
          grid-column: 1 / span 2;
          grid-row: 3;
        `;
      case 'south':
        return `
          display: flex;
          flex-wrap: wrap;
          grid-column: 1 / span 2;
          grid-row: 4;
        `;
      case 'west':
        return `
          grid-column: 1;
          grid-row: 3;
        `;
      case 'east':
        return `
          grid-column: 2;
          grid-row: 3;
        `;
    }
  }}
`;

export const PlotLayout = ({
  fontSize,
  Graph,
  handleHover,
  Legend,
  legendPosition,
  loading,
  numLines,
  showLegend,
  Title,
  Warning,
  ...props
}: PlotLayoutProps) => {
  // the legend and the warning can both resize, so we need to track them both
  const {
    height: legendHeight,
    ref: legendRef,
    width: legendWidth,
  } = useResizeObserver<HTMLDivElement>();
  const {
    height: warningHeight,
    ref: warningRef,
    width: warningWidth,
  } = useResizeObserver<HTMLDivElement>();
  const [refreshKey, setRefreshKey] = React.useState(0);

  /**
   * Notes: this is a little hacky
   *
   * Problem: React vis' FlexibleXYPlot has to do a bit of internal magic to figure out how to size itself. It seems like there can be a bit of a lag between how the CSS grid/flexbox sizing updates and how this hooks into the React render lifecycle. A problem arises when React vis "holds on" to the sizing from the last frame and fails to update all of its layers correctly. This can cause overflows or the highlighted layer to not display at the exact same dimensions.
   *
   * This problem fixes itself whenever something triggers a new render pass and the measuring updates. So the fix (until we get rid of FlexibleXYPlot in the near future) is to force React Vis to do another render pass when something changes size. Doing this too much will absolutely TANK performance in the view since we load a lot of React vis charts with a lot of lines and repainting all the layers is expensive.
   *
   * This fix measures the size of thet two sibling elements that can flex size during the lifecycle:
   * - the legend warning, which can pop in or out depending on how many lines are rendering, and
   * - the legend itself, which can pop in and out based on its own display rules and the panel config
   *
   * When these dimensions change we're generating a new `key` on the wrapper. This forces everything downstream to reset and re-render. `useLayoutEffect` is used to make sure this happens before the browser paints the next frame.
   */
  React.useLayoutEffect(() => {
    setRefreshKey(Date.now());
  }, [legendHeight, warningHeight, legendWidth, warningWidth, setRefreshKey]);

  // display: contents keeps tailwind from interfering with the CSS grid
  return (
    <TailwindContents>
      <Wrapper
        fontSize={fontSize}
        key="plot-layout"
        onMouseEnter={() => handleHover(true)}
        onMouseLeave={() => handleHover(false)}
        legendPosition={legendPosition}
        showLegend={showLegend}
        {...props}>
        <div
          className="col-span-2 col-start-1 row-start-1 overflow-hidden"
          data-testid="title-wrapper">
          {Title}
        </div>
        <div
          className="col-span-2 col-start-1 row-start-2 flex justify-center overflow-hidden"
          data-testid="warning-wrapper"
          ref={warningRef}>
          {Warning}
        </div>

        <GraphWrapper
          legendPosition={legendPosition}
          key={refreshKey}
          showLegend={showLegend}>
          {Graph}
        </GraphWrapper>
        {showLegend && (
          <LegendWrapper legendPosition={legendPosition} ref={legendRef}>
            {Legend}
          </LegendWrapper>
        )}

        {loading && (
          <>
            {numLines > 0 ? (
              <div className="absolute z-10">
                <InstrumentedWaveLoader
                  size="small"
                  name="panel-layout-loading-small"
                />
              </div>
            ) : (
              <div className="absolute flex h-full w-full items-center justify-center">
                <InstrumentedWaveLoader
                  name="panel-layout-loading-huge"
                  size="huge"
                />
              </div>
            )}
          </>
        )}
      </Wrapper>
    </TailwindContents>
  );
};
